import { gsap } from "gsap";
import React, { forwardRef, useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Methodology = (props, ref) => {
	const bg = useRef();
	const subtitle = useRef();
	const heading = useRef();
	const copy = useRef();

	let scrollTl = null;

	useEffect(() => {
		scrollTl = gsap.timeline({
			scrollTrigger: {
				trigger: ref.current,
				invalidateOnRefresh: true,
				start: () => `top center`,
				markers: false,
				scrub: false, // smooth scrubbing, takes 1 second to "catch up" to the scrollbar
			},
		});

		scrollTl
			.fromTo(
				bg.current,
				{ scaleY: 0 },
				{ scaleY: 1, transformOrigin: "top", duration: 2, ease: "Power.inOut" },
				0
			)
			.fromTo(
				[subtitle.current, heading.current, copy.current],
				{ autoAlpha: 0 },
				{ autoAlpha: 1, stagger: 0.12, duration: 1, ease: "Power2.inOut" },
				0.16
			);
	});

	return (
		<div className="about-methodology" ref={ref}>
			<div className="about-methodology__inner">
				<p className="about-methodology__subtitle" ref={subtitle}>
					Methodology
				</p>
				<h2 className="about-methodology__heading" ref={heading}>
					The short answer—a crippling amount of spreadsheets. The long one? Settle in…
				</h2>
				<div className="about-methodology__copy" ref={copy}>
					<p>
						Every year we craft a bespoke survey for our WeTransfer community. Each survey tends to be
						centred around a different theme—from what makes an idea worth pursuing to the impact of a
						pandemic on our creativity (shoutout 2020)—and includes a bunch of demographic questions so we
						can segment our findings by things like age, location, or creative field.
					</p>
					<p>
						The survey is then linked to a WeTransfer wallpaper—one of those big background images you see
						on wetransfer.com—where we invite anyone who lands on the page to take part. This wallpaper runs
						for around four to six weeks, and during that time we’ll receive thousands of responses from
						hundreds of countries, painting a pretty strong picture of a global creative outlook.
					</p>
					<p>
						Once the answers are in, we whip out the spreadsheets, dive into the data, and make note of any
						key insights or interesting trends. For example, the creative field most likely to get their
						best ideas in the bathroom (we’re looking at you Advertising), or the only place where booze
						actually increases creativity (cheers to that Japan).
					</p>
					<p>
						As overarching narratives begin to emerge, we ask some of our creative friends to weigh in on
						the findings. Over the years, this has included superb insights from the likes of Roxane Gay,
						John Legend, and neurosurgical professor, Lu Chen.
					</p>
					<p>
						The final challenge is to bring everything together in an informative yet engaging experience,
						visualising our findings in anything from short videos and animated graphs to personalized
						creative horoscopes. None of which would be possible without our disgustingly talented
						WeTransfer Studio and an outstanding collection of partners.
					</p>
				</div>
				<div className="about-methodology__bg" ref={bg}></div>
			</div>
		</div>
	);
};

export default forwardRef(Methodology);
